import {Component} from 'panel';

import {defineAndMakeJsxFactory} from 'common/util/jsx';
import {BuilderType, FrequencyOperator} from 'common/widgets/cohorts-query-builder/models/enums/builder-enums';
import {BehaviorCountType} from 'common/widgets/cohorts-query-builder/models/enums/expression-enums';
import {DEFAULT_FREQUENCY_VALUE} from 'common/widgets/cohorts-query-builder/models/constants';
import {pluralize, replaceByIndex} from 'common/widgets/cohorts-query-builder/util/index';
import {TrackingEvent} from 'common/widgets/cohorts-query-builder/util/tracking';

import template from './index.jade';
import css from './index.styl?inline';

export class CohortsClauseFrequencies extends Component {
  static get attrsSchema() {
    return {
      ...super.attrsSchema,
      'user-id': {type: `number`, description: `ID of user viewing the builder`},
    };
  }

  shouldUpdate(state) {
    return this.$panelParent.shouldUpdate(state);
  }

  get config() {
    return {
      useShadowDom: true,
      css,
      template,
      helpers: {
        getFrequencyState: (frequencyIdx) => this.frequencyBuilderState[frequencyIdx],
        getFrequencyValue: (frequencyIdx) => this.frequency[frequencyIdx],
        isDistinctDayEnabled: () => {
          const isFunnel = this.state.stagedCohort.literals[this.literalIdx].isFunnel;
          return !isFunnel && (this.hasTwoFrequencies || this.currentFirstFrequency() > 1);
        },
        clickedFrequencyLabel: (frequencyIdx) => {
          const frequencyState = {
            initialInputWidth: [...this.el.querySelectorAll(`.cohorts-frequency-value`)][frequencyIdx].scrollWidth,
            isEditing: true,
          };
          this.app.updateStagedCohort(this.setFrequencyState(frequencyState, frequencyIdx));
        },
        clickedTimeLabel: () => {
          this.app.startClauseBuilderAtPane(
            `cohorts-builder-distinct-day`,
            BuilderType.DistinctDay,
            this.clausePosition,
          );
        },
        handleInput: (ev, frequencyIdx) => {
          const inputText = ev.target.value;
          this.app.updateStagedCohort(this.setFrequencyState({inputText}, frequencyIdx));
        },
        changedFrequencyValue: (ev, frequencyIdx) => {
          const inputText = ev.target.value;
          const newFrequency = this.getFrequencyValue(inputText);
          this.app.track(
            TrackingEvent.UpdateFrequency,
            Object.assign({}, this.state.stagedCohort.getTrackingDataForClause(this.clausePosition), {
              frequency: newFrequency,
            }),
          );
          const stagedCohort = this.state.stagedCohort.updateFrequencyValue(
            replaceByIndex(this.frequency, frequencyIdx, newFrequency),
            this.clausePosition,
          );
          this.app.updateStagedCohort(stagedCohort);
        },
        handleFocusout: (frequencyIdx) => {
          const frequencyState = {
            inputText: ``,
            isEditing: false,
          };
          let stagedCohort = this.setFrequencyState(frequencyState, frequencyIdx);
          if (!this.helpers.isDistinctDayEnabled()) {
            stagedCohort = stagedCohort.updateBehaviorCountType(BehaviorCountType.Absolute, this.clausePosition);
          }
          this.app.updateStagedCohort(stagedCohort);
        },
        isBehaviorCountTypeBuilderOpen: () => this.app.isBuilderOpen(BuilderType.DistinctDay, this.clausePosition),
        pluralizedTimeLabel: () =>
          this.hasTwoFrequencies ? `times` : pluralize(`time`, this.currentFirstFrequency(), `times`),
        shouldShowSecondFrequency: () => this.hasTwoFrequencies,
        selectedBehaviorCountType: (ev) => {
          const {stagedCohort} = this.state;
          const behaviorCountType = ev.detail.value;
          this.app.track(
            TrackingEvent.SelectCountType,
            Object.assign({}, stagedCohort.getTrackingDataForClause(this.clausePosition), {type: behaviorCountType}),
          );
          this.app.closeBuilder(BuilderType.DistinctDay, this.clausePosition);
          this.app.updateStagedCohort(stagedCohort.updateBehaviorCountType(behaviorCountType, this.clausePosition));
        },
      },
    };
  }

  get clauseIdx() {
    return Number(this.dataset.clauseIdx);
  }

  get literalIdx() {
    return Number(this.dataset.literalIdx);
  }

  get clausePosition() {
    return {
      clauseIdx: this.clauseIdx,
      literalIdx: this.literalIdx,
    };
  }

  get frequencyBuilderState() {
    return this.state.stagedCohort.getFrequencyClauseBuilderState(this.clausePosition);
  }

  get frequency() {
    return this.state.stagedCohort.getFrequency(this.clausePosition);
  }

  get hasTwoFrequencies() {
    return this.state.stagedCohort.getFrequencyOperator(this.clausePosition) === FrequencyOperator.Between;
  }

  getFrequencyValue(inputText) {
    let frequency = parseInt(inputText, 10);
    if (Number.isNaN(frequency) || frequency < 1) {
      frequency = DEFAULT_FREQUENCY_VALUE;
    }
    return frequency;
  }

  currentFirstFrequency() {
    const firstInputState = this.frequencyBuilderState[0];
    if (firstInputState.isEditing && firstInputState.inputText !== ``) {
      return this.getFrequencyValue(firstInputState.inputText);
    } else {
      return this.frequency[0];
    }
  }

  setFrequencyState(attrs, frequencyIdx) {
    const frequencyBuilderState = replaceByIndex(this.frequencyBuilderState, frequencyIdx, {
      ...this.frequencyBuilderState[frequencyIdx],
      ...attrs,
    });
    return this.state.stagedCohort.updateFrequencyState(frequencyBuilderState, this.clausePosition);
  }
}

export const CohortsClauseFrequenciesEl = defineAndMakeJsxFactory(
  `cohorts-clause-frequencies`,
  CohortsClauseFrequencies,
);
